import { Component, ErrorInfo, ReactNode } from 'react'
import { Box, Button, Typography } from '@mui/material'

interface Props {
  children: ReactNode
  // Names the area that failed, e.g. 'This page could not be displayed'.
  title?: string
}

interface State {
  hasError: boolean
}

// Class component is required here: getDerivedStateFromError / componentDidCatch
// have no hook equivalent, so this is the one place the arrow-function convention
// cannot apply.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // No error tracker is wired up in this project yet, so the stack is surfaced in
    // development only and production stays quiet. Send it to the tracker here once
    // one exists — otherwise a crash leaves no trace at all.
    if (process.env.NODE_ENV === 'development') {
      console.error('Unhandled render error:', error, errorInfo.componentStack)
    }
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <Box
        className='f f-d-c f-ai-c f-jc-c p-x-xl p-y-xl'
        data-testid='error-boundary'
      >
        <Typography
          variant='h3'
          className='m-b-md'
        >
          {this.props.title ?? 'Something went wrong'}
        </Typography>
        <Typography
          className='m-b-lg'
          color='text.secondary'
        >
          This section could not be displayed. Your last action may not have been saved — please check before retrying it.
        </Typography>
        <Button
          variant='contained'
          onClick={() => window.location.reload()}
          data-testid='error-boundary-reload'
        >
          Reload
        </Button>
      </Box>
    )
  }
}
